/**
 * Lifecycle of an `Abrechnungslauf` — the §302 run (TM-9538 / #1519).
 *
 * - `PENDING` — created with its items, awaiting worker pickup.
 * - `IN_PROGRESS` — the worker has begun building files.
 * - `COMPLETED` — every file and every item reached a terminal state. Partial
 *   failures do **not** promote the run to `FAILED`: one file that fails does
 *   not hold the others up, and the ones that were written are on their way.
 * - `CANCELED` — user-initiated abort while still `PENDING` or `IN_PROGRESS`.
 * - `FAILED` — the sweeper-only catastrophic path (a run left `IN_PROGRESS`
 *   past the orphan threshold). User-facing commands cannot reach it.
 *
 * ⚠ **There is no preview, dry-run or draft state, and none may be added.** The
 * Vorschau is the read model of #1511 and creates no aggregate; a *„Probelauf"*
 * in §302 is something else entirely — the Testindikator (#1523), which is a
 * property of `(IK × Kostenträger-IK)` and not a stage of this machine. A draft stage
 * here would be a run that burned counters nobody could account for.
 *
 * Same five values as `DunningRunStatus` and `InvoiceRunStatus`, deliberately:
 * what is identical across the three batch runs is identical on purpose.
 */
export var GkvBillingRunStatus;
(function (GkvBillingRunStatus) {
    GkvBillingRunStatus["PENDING"] = "pending";
    GkvBillingRunStatus["IN_PROGRESS"] = "in_progress";
    GkvBillingRunStatus["COMPLETED"] = "completed";
    GkvBillingRunStatus["CANCELED"] = "canceled";
    GkvBillingRunStatus["FAILED"] = "failed";
})(GkvBillingRunStatus || (GkvBillingRunStatus = {}));
/**
 * Is this run finished, whichever way it finished?
 *
 * ⚠ **One definition, because „terminal" decides three different things** — when
 * the status poll stops, whether the Abbrechen button is offered, and which
 * headline the run carries. Spelled out separately at each of them, the three
 * drift the day a sixth status arrives: the poll would keep asking after a run
 * nobody can act on any more, silently and for as long as the tab is open.
 *
 * ⚠ **`FAILED` is terminal**, and it is the one a *„fertig heißt COMPLETED"*
 * reading would miss. It is the sweeper's state: the run is over, its Fälle are
 * free, and nothing further will happen to it.
 */
export function isGkvBillingRunTerminal(status) {
    return (status !== undefined &&
        status !== GkvBillingRunStatus.PENDING &&
        status !== GkvBillingRunStatus.IN_PROGRESS);
}
/**
 * What each status is called on screen.
 *
 * ⚠ **`FAILED` is *„liegen geblieben"*, not *„fehlgeschlagen"*.** It is reached
 * only by the sweeper, which means no Fall threw anything — the process died.
 * Calling it a failure sends somebody looking for a defect that does not exist,
 * and hides the one thing that actually happened: nobody was watching for a day.
 */
export const GKV_BILLING_RUN_STATUS_LABEL = {
    [GkvBillingRunStatus.PENDING]: 'wartet',
    [GkvBillingRunStatus.IN_PROGRESS]: 'läuft',
    [GkvBillingRunStatus.COMPLETED]: 'abgeschlossen',
    [GkvBillingRunStatus.CANCELED]: 'abgebrochen',
    [GkvBillingRunStatus.FAILED]: 'liegen geblieben',
};
/**
 * The icon each status carries — one map, so the Historie and the
 * Fortschrittsansicht cannot disagree about what a cancelled run looks like.
 */
export const GKV_BILLING_RUN_STATUS_ICON = {
    [GkvBillingRunStatus.PENDING]: 'icon-[mingcute--time-line] text-tm-gray-600',
    [GkvBillingRunStatus.IN_PROGRESS]: 'icon-[mingcute--time-line] text-tm-gray-600',
    [GkvBillingRunStatus.COMPLETED]: 'icon-[mingcute--check-circle-line] text-tm-petrol-base',
    [GkvBillingRunStatus.CANCELED]: 'icon-[mingcute--minus-circle-line] text-tm-gray-600',
    [GkvBillingRunStatus.FAILED]: 'icon-[mingcute--close-circle-line] text-tm-red-highlight',
};
/**
 * The name of a run's file package (TM-9540 / #1555).
 *
 * ⚠ **One place for both sides.** The server sets it in `Content-Disposition`,
 * the browser writes it to disk — two computations of the same name drift apart
 * at the first format change, and the practice would then have two names for the
 * same Lieferung.
 *
 * ⚠ **The day is the *local* day, not the UTC day.** Every Node process here
 * runs with `TZ=Europe/Berlin`; a `toISOString().slice(0, 10)` would name a run
 * that finished at 00:30 Berlin time after the previous day, and that is exactly
 * the run that lies in the Übergabeverzeichnis the next morning.
 *
 * German, because a human reads it.
 */
export function gkvBillingRunZipFilename(producedAt) {
    return `GKV-Abrechnungslauf-${gkvBillingRunDay(producedAt)}.zip`;
}
/** `JJJJ-MM-TT` des Lauftags, in der Zeitzone des Prozesses. */
export function gkvBillingRunDay(producedAt) {
    const pad = (value) => String(value).padStart(2, '0');
    return `${producedAt.getFullYear()}-${pad(producedAt.getMonth() + 1)}-${pad(producedAt.getDate())}`;
}
//# sourceMappingURL=gkv-billing-run-status.enum.js.map